import { Button, Text, makeStyles, tokens } from "@fluentui/react-components";
import {
  Checkmark20Regular,
  DataTrending20Regular,
  ShieldCheckmark16Regular,
  TaskListSquareLtr20Regular,
} from "@fluentui/react-icons";
import { APP_VERSION_LABEL } from "../config/appVersion";
import kationLogo from "../assets/brand/kation-logo-white.png";

// The sign-in screen. A brand-led split: the Kation canvas (deep navy, always
// dark by design) carries the identity; the right panel carries the single
// action. The right panel uses Fluent tokens so it follows light/dark theme.
const useStyles = makeStyles({
  root: {
    minHeight: "100vh",
    display: "grid",
    gridTemplateColumns: "66fr 34fr",
    "@media (max-width: 820px)": { gridTemplateColumns: "1fr" },
  },

  // ---- brand canvas (committed dark navy in both themes) ----
  canvas: {
    position: "relative",
    overflow: "hidden",
    isolation: "isolate",
    color: "#EAF2FB",
    padding: "48px 56px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    gap: "40px",
    backgroundImage:
      "radial-gradient(120% 120% at 8% 10%, #0d3a63 0%, transparent 42%), linear-gradient(150deg, #0A2A4A 0%, #071E36 100%)",
    "@media (max-width: 820px)": { padding: "40px 32px", minHeight: "300px" },
  },
  grid: {
    content: '""',
    position: "absolute",
    inset: 0,
    zIndex: -1,
    backgroundImage:
      "linear-gradient(rgba(157,182,208,0.20) 1px, transparent 1px), linear-gradient(90deg, rgba(157,182,208,0.20) 1px, transparent 1px)",
    backgroundSize: "46px 46px",
    maskImage: "radial-gradient(120% 90% at 20% 20%, #000 40%, transparent 78%)",
    WebkitMaskImage:
      "radial-gradient(120% 90% at 20% 20%, #000 40%, transparent 78%)",
    opacity: 0.5,
  },
  blob: { position: "absolute", zIndex: -1, borderRadius: "50%", filter: "blur(46px)" },
  blobA: {
    width: "340px",
    height: "340px",
    right: "-70px",
    top: "-60px",
    opacity: 0.5,
    backgroundImage: "radial-gradient(circle, #1BA1E2, transparent 68%)",
    animationName: { "0%,100%": { transform: "translate(0,0) scale(1)" }, "50%": { transform: "translate(-26px,22px) scale(1.08)" } },
    animationDuration: "18s",
    animationTimingFunction: "ease-in-out",
    animationIterationCount: "infinite",
    "@media (prefers-reduced-motion: reduce)": { animationName: "none" },
  },
  blobB: {
    width: "300px",
    height: "300px",
    right: "40px",
    bottom: "-90px",
    opacity: 0.45,
    backgroundImage: "radial-gradient(circle, #1268C3, transparent 66%)",
    animationName: { "0%,100%": { transform: "translate(0,0) scale(1)" }, "50%": { transform: "translate(22px,-18px) scale(1.1)" } },
    animationDuration: "22s",
    animationTimingFunction: "ease-in-out",
    animationIterationCount: "infinite",
    "@media (prefers-reduced-motion: reduce)": { animationName: "none" },
  },

  logo: { position: "relative" },
  logoImg: { height: "104px", width: "auto", display: "block" },

  promo: { position: "relative", maxWidth: "34ch" },
  kicker: {
    display: "inline-block",
    fontSize: "0.72rem",
    letterSpacing: "0.18em",
    textTransform: "uppercase",
    fontWeight: 700,
    color: "#F6A623",
    borderLeft: "2px solid #F6A623",
    paddingLeft: "10px",
    marginBottom: "18px",
  },
  headline: {
    fontSize: "clamp(2.4rem, 1.4rem + 3vw, 3.9rem)",
    lineHeight: 1.03,
    letterSpacing: "-0.025em",
    fontWeight: 700,
    margin: 0,
    marginBottom: "16px",
    color: "#FFFFFF",
    textWrap: "balance",
  },
  headlineEm: { color: "#2E9BE0", fontStyle: "normal" },
  lede: { fontSize: "1.05rem", color: "#9DB6D0", margin: 0, maxWidth: "34ch" },

  feats: { position: "relative", display: "flex", flexDirection: "column", gap: "12px" },
  feat: { display: "flex", alignItems: "center", gap: "12px", fontSize: "0.94rem", color: "#C9DAEC" },
  featIcon: { color: "#2E9BE0", flexShrink: 0 },

  // ---- right sign-in panel (form sits directly on the panel — no card) ----
  panel: {
    backgroundColor: tokens.colorNeutralBackground1,
    padding: "40px 48px",
    display: "flex",
    flexDirection: "column",
    "@media (max-width: 820px)": { padding: "40px 28px" },
  },
  panelBody: {
    flexGrow: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
  },
  form: {
    width: "100%",
    maxWidth: "340px",
    display: "flex",
    flexDirection: "column",
  },
  eyebrow: {
    display: "block",
    fontSize: "0.72rem",
    letterSpacing: "0.15em",
    textTransform: "uppercase",
    fontWeight: 600,
    color: tokens.colorNeutralForeground3,
    marginBottom: "22px",
  },
  signInTitle: { display: "block", marginBottom: "8px" },
  signInLede: {
    display: "block",
    color: tokens.colorNeutralForeground3,
    marginBottom: "30px",
  },
  msBtn: {
    width: "100%",
    justifyContent: "center",
    gap: "12px",
    height: "48px",
    fontSize: "1rem",
  },
  msLogo: {
    width: "20px",
    height: "20px",
    flexShrink: 0,
    backgroundColor: "#fff",
    borderRadius: "3px",
    padding: "2px",
    boxSizing: "border-box",
  },
  secure: {
    display: "flex",
    alignItems: "flex-start",
    gap: "9px",
    marginTop: "18px",
    color: tokens.colorNeutralForeground3,
    fontSize: "0.82rem",
    lineHeight: 1.45,
  },
  secureIcon: { color: tokens.colorBrandForeground1, flexShrink: 0, marginTop: "2px" },
  help: {
    marginTop: "28px",
    paddingTop: "22px",
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    color: tokens.colorNeutralForeground3,
    fontSize: "0.82rem",
    lineHeight: 1.5,
  },
  foot: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    color: tokens.colorNeutralForeground4,
    fontSize: "0.76rem",
    paddingTop: "12px",
  },
});

const MicrosoftMark = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 23 23" aria-hidden="true">
    <rect width="10" height="10" x="1" y="1" fill="#F25022" />
    <rect width="10" height="10" x="12" y="1" fill="#7FBA00" />
    <rect width="10" height="10" x="1" y="12" fill="#00A4EF" />
    <rect width="10" height="10" x="12" y="12" fill="#FFB900" />
  </svg>
);

export function Login({ onSignIn }: { onSignIn: () => void }) {
  const s = useStyles();
  return (
    <div className={s.root}>
      <div className={s.canvas}>
        <div className={s.grid} />
        <div className={s.blob + " " + s.blobA} />
        <div className={s.blob + " " + s.blobB} />

        <div className={s.logo}>
          <img className={s.logoImg} src={kationLogo} alt="Kation Technologies" />
        </div>

        <div className={s.promo}>
          <span className={s.kicker}>Employee Self-Service</span>
          <h1 className={s.headline}>
            Your time off, <em className={s.headlineEm}>handled.</em>
          </h1>
          <p className={s.lede}>
            File leave, watch your balances, and clear approvals — one place, one
            sign-in.
          </p>
        </div>

        <div className={s.feats}>
          <div className={s.feat}>
            <Checkmark20Regular className={s.featIcon} />
            Single sign-on with Microsoft 365
          </div>
          <div className={s.feat}>
            <DataTrending20Regular className={s.featIcon} />
            Live leave balances, always current
          </div>
          <div className={s.feat}>
            <TaskListSquareLtr20Regular className={s.featIcon} />
            Manager approvals in a click
          </div>
        </div>
      </div>

      <div className={s.panel}>
        <div className={s.panelBody}>
          <div className={s.form}>
            <span className={s.eyebrow}>KTI Leave Management</span>
            <Text as="h1" size={900} weight="bold" className={s.signInTitle}>
              Sign in
            </Text>
            <Text size={400} className={s.signInLede}>
              Access the Employee Self-Service portal with your Kation account.
            </Text>

            <Button
              appearance="primary"
              size="large"
              className={s.msBtn}
              icon={<MicrosoftMark className={s.msLogo} />}
              onClick={onSignIn}
            >
              Sign in with Microsoft
            </Button>

            <div className={s.secure}>
              <ShieldCheckmark16Regular className={s.secureIcon} />
              <span>
                Secured by Microsoft Entra ID. Single sign-on — your password is
                entered on Microsoft&rsquo;s page, never stored here.
              </span>
            </div>

            <span className={s.help}>
              Trouble signing in? Use your Kation
              (@kationtechnologies.com) account, or contact your KTI IT
              administrator.
            </span>
          </div>
        </div>

        <div className={s.foot}>
          <span>© Kation Technologies, Inc.</span>
          <span>{APP_VERSION_LABEL}</span>
        </div>
      </div>
    </div>
  );
}
